import {
  Box,
  Button,
  useTheme,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  Fade,
  Divider,
  Skeleton,
  Collapse,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import { tokens } from "../../theme";
import React, { useEffect, useMemo, useCallback, useState } from "react";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Header from "../../components/Header";
import TuneIcon from "@mui/icons-material/Tune";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { useLocation } from "react-router-dom";

import {
  listExpensesRecord,
  getExpensesRecord,
  deleteExpensesRecord,
  deleteAllExpensesRecords,
} from "../../services/ExpenseService";
import ExpensesRecordForm from "../../components/Expenses/ExpensesRecordForm";
import ExpensesDeleteDialog from "../../components/Expenses/ExpensesDeleteDialog";
import { isAdmin } from "../../services/UserService";
import CustomNoRowsOverlay from "../../components/CustomNoRowsOverlay";

const CustomToolbar = ({ searchQuery, setSearchQuery, colors }) => {
  const theme = useTheme();

  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        gap: "16px",
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        flexWrap={{ xs: "nowrap", sm: "wrap" }}
        gap={{ xs: 0, sm: 1 }}
        sx={{
          width: { xs: "100%", sm: "100%", md: "auto" },
          justifyContent: {
            xs: "space-between",
            sm: "space-between",
            md: "flex-start",
          },
        }}
      >
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </Box>

      <Box
        display="flex"
        flexGrow={1}
        justifyContent="flex-end"
        minWidth={{ xs: "100%", sm: "250px" }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.gray[100] }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: { md: "300px" },
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : colors.primary[400],
            borderRadius: "4px",
            transition: "all 0.2s ease",
            "& .MuiOutlinedInput-root": {
              color: colors.gray[100],
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0,0,0,0.15)",
              },
              "&:hover fieldset": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : colors.gray[500],
              },
              "&.Mui-focused fieldset": { borderColor: colors.blueAccent[500] },
            },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );
};

const ActionMenuCell = React.memo(function ActionMenuCell({
  row,
  colors,
  theme,
  onEdit,
  onDelete,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      height="100%"
    >
      <IconButton
        onClick={handleClick}
        sx={{
          color: colors.gray[100],
          transition: "transform 0.15s ease",
          "&:active": { transform: "scale(0.9)" },
        }}
        aria-controls={open ? "action-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        id="action-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        transitionDuration={300}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(3px)",
              backgroundColor: "rgba(0,0,0,0.1)",
            },
          },
        }}
        PaperProps={{
          sx: {
            backgroundColor:
              theme.palette.mode === "dark"
                ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                : "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(16px)",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
            borderRadius: "12px",
            minWidth: "150px",
            mt: 1,
            "& .MuiMenuItem-root": {
              padding: "12px 20px",
              gap: "12px",
              margin: "4px 8px",
              borderRadius: "8px",
              transition: "all 0.2s ease-in-out",
              color: colors.gray[100],
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.04)",
              },
              "&:active": { transform: "scale(0.95)" },
            },
            "& .MuiDivider-root": {
              margin: "4px 8px",
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.1)"
                  : "rgba(0, 0, 0, 0.1)",
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            onEdit(row);
            handleClose();
          }}
        >
          <EditOutlinedIcon fontSize="small" />
          <Typography fontSize="14px" fontWeight="600">
            Edit
          </Typography>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            onDelete(row.id);
            handleClose();
          }}
          sx={{
            color: `${colors.redAccent[500]} !important`,
            "&:hover": {
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(244, 63, 94, 0.1) !important"
                  : "rgba(225, 29, 72, 0.1) !important",
            },
          }}
        >
          <DeleteOutlinedIcon fontSize="small" />
          <Typography fontSize="14px" fontWeight="600">
            Delete
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
});

const Expenses = () => {
  const theme = useTheme();
  const colors = useMemo(
    () => tokens(theme.palette.mode),
    [theme.palette.mode],
  );

  const [rawData, setRawData] = useState([]);
  const [rows, setRows] = useState([]);
  const [filterType, setFilterType] = useState("month");
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [totalBalanceSum, setTotalBalanceSum] = useState(0);
  const [editRecord, setEditRecord] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const location = useLocation();
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const [fadeOut, setFadeOut] = useState(false);

  const admin = isAdmin();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditRecord(null);
  };

  const handleOpenDialog = useCallback((id = null) => {
    setDeleteRecord(id);
    setIsBulkDelete(id === null);
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    setIsLoading(true);
    listExpensesRecord()
      .then((response) => {
        const data = response.data.map((item) => ({
          id: item.id,
          ...item,
        }));
        setRawData(data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!rawData) return;
    setIsLoading(true);
    setRows([]);

    const timer = setTimeout(() => {
      let filtered = [...rawData];

      filtered.sort((a, b) => b.id - a.id);

      if (filterType !== "all") {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        if (filterType === "today") {
          filtered = filtered.filter((item) => {
            if (!item.date) return false;
            const itemDate = new Date(item.date);
            itemDate.setHours(0, 0, 0, 0);
            return itemDate.getTime() === now.getTime();
          });
        } else {
          const cutoff = new Date();
          if (filterType === "week") {
            cutoff.setDate(cutoff.getDate() - 7);
          } else if (filterType === "14days") {
            cutoff.setDate(cutoff.getDate() - 14);
          } else if (filterType === "month") {
            cutoff.setDate(cutoff.getDate() - 30);
          } else if (filterType === "3months") {
            cutoff.setMonth(cutoff.getMonth() - 3);
          } else if (filterType === "6months") {
            cutoff.setMonth(cutoff.getMonth() - 6);
          } else if (filterType === "year") {
            cutoff.setFullYear(cutoff.getFullYear() - 1);
          }
          filtered = filtered.filter((item) => new Date(item.date) >= cutoff);
        }
      }

      if (searchQuery) {
        const lowerCaseQuery = searchQuery.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.title?.toLowerCase().includes(lowerCaseQuery) ||
            item.type?.toLowerCase().includes(lowerCaseQuery) ||
            item.supplierName?.toLowerCase().includes(lowerCaseQuery) ||
            item.expensesDesc?.toLowerCase().includes(lowerCaseQuery) ||
            item.enteredBy?.toLowerCase().includes(lowerCaseQuery) ||
            item.id?.toString().includes(lowerCaseQuery),
        );
      }

      const dynamicSum = filtered.reduce(
        (acc, curr) => acc + (Number(curr.totalBalance) || 0),
        0,
      );

      setRows(filtered);
      setTotalBalanceSum(dynamicSum);
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [rawData, filterType, searchQuery]);

  useEffect(() => {
    if (location.state && location.state.id) {
      const targetId = location.state.id;
      setHighlightedRowId(targetId);

      setTimeout(() => {
        document.querySelector(`[data-id="${targetId}"]`)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 500);
    }
  }, [location]);

  useEffect(() => {
    if (highlightedRowId) {
      const fadeTimeout = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => {
          setHighlightedRowId(null);
          setFadeOut(false);
        }, 300);
      }, 2500);

      return () => clearTimeout(fadeTimeout);
    }
  }, [highlightedRowId]);

  const getRowClassName = (params) => {
    if (params.id === highlightedRowId) {
      return fadeOut ? "highlighted-row fade-out" : "highlighted-row";
    }
    return "";
  };

  const handleAddRecord = (newExpensesRecord) => {
    setRawData((prev) => [...prev, newExpensesRecord]);
  };

  const handleEditRecord = useCallback((row) => {
    getExpensesRecord(row.id)
      .then((response) => {
        setEditRecord(response.data);
        setOpen(true);
      })
      .catch((error) => console.error("Error fetching record by ID: ", error));
  }, []);

  const handleDeleteRecord = async () => {
    if (isBulkDelete) {
      try {
        await deleteAllExpensesRecords();
        setRawData([]);
      } catch (error) {
        console.error("Error deleting all records: ", error);
        throw error;
      }
    } else if (deleteRecord) {
      try {
        await deleteExpensesRecord(deleteRecord);
        setRawData((prev) => prev.filter((row) => row.id !== deleteRecord));
        setDeleteRecord(null);
      } catch (error_1) {
        console.error("Error deleting record: ", error_1);
        throw error_1;
      }
    }
    return Promise.resolve();
  };

  const handleRecordEdited = (updatedRecord) => {
    setRawData((prev) =>
      prev.map((record) =>
        record.id === updatedRecord.id
          ? { ...record, ...updatedRecord }
          : record,
      ),
    );
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", flex: 0.25, minWidth: 70 },
      { field: "title", headerName: "Title", flex: 0.5, minWidth: 120 },
      { field: "type", headerName: "Type", flex: 0.5, minWidth: 100 },
      {
        field: "quantity",
        headerName: "Quantity",
        flex: 0.5,
        minWidth: 110,
        valueFormatter: (params) => params.toLocaleString(),
      },
      {
        field: "enteredBy",
        headerName: "Entered By",
        flex: 0.5,
        minWidth: 130,
      },
      {
        field: "cost",
        headerName: "Cost",
        flex: 0.5,
        minWidth: 100,
        valueFormatter: (params) =>
          new Intl.NumberFormat("en-NG", {
            style: "currency",
            currency: "NGN",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(params),
      },
      {
        field: "expensesDesc",
        headerName: "Description",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "supplierName",
        headerName: "Supplier",
        flex: 0.5,
        minWidth: 110,
      },
      {
        field: "date",
        headerName: "Date",
        flex: 0.5,
        minWidth: 110,
        type: "date",
        valueFormatter: (params) => {
          if (!params) return "";
          const date = new Date(params);
          if (isNaN(date.getTime())) return params;
          const dd = String(date.getDate()).padStart(2, "0");
          const mm = String(date.getMonth() + 1).padStart(2, "0");
          const yyyy = date.getFullYear();
          return `${dd}-${mm}-${yyyy}`;
        },
      },
      {
        field: "totalBalance",
        headerName: "Total Balance",
        flex: 0.75,
        minWidth: 120,
        valueFormatter: (params) =>
          new Intl.NumberFormat("en-NG", {
            style: "currency",
            currency: "NGN",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(params),
      },
      ...(admin
        ? [
            {
              field: "actions",
              headerName: "Actions",
              headerAlign: "center",
              flex: 0.5,
              minWidth: 80,
              renderCell: (params) => (
                <ActionMenuCell
                  row={params.row}
                  colors={colors}
                  theme={theme}
                  onEdit={handleEditRecord}
                  onDelete={handleOpenDialog}
                />
              ),
            },
          ]
        : []),
    ],
    [colors, theme, admin, handleEditRecord, handleOpenDialog],
  );

  return (
    <Box
      m={{ xs: "10px", sm: "15px", md: "20px" }}
      sx={{
        "@keyframes slideInUp": {
          "0%": { opacity: 0, transform: "translateY(20px)" },
          "100%": { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        flexWrap={{ xs: "wrap", md: "nowrap" }}
        gap="15px"
        mb={{ xs: "20px", md: "40px" }}
        sx={{
          animation: "slideInUp 0.5s ease-out forwards",
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          sx={{
            flexShrink: 1,
            minWidth: 0,
            width: { xs: "100%", md: "auto" },
            "& > div": { mb: "0 !important" },
            "& h2": {
              fontSize: { xs: "1.5rem", md: "1.5rem", lg: "2rem" },
              mb: "4px !important",
              mt: "0 !important",
              lineHeight: "1 !important",
            },
            "& h5": {
              fontSize: { xs: "0.75rem", md: "0.75rem", lg: "0.875rem" },
              mt: "0 !important",
              mb: "0 !important",
              lineHeight: "1 !important",
            },
          }}
        >
          <Header
            title="EXPENSES"
            subtitle="Expenses transactions listed here"
          />
        </Box>

        {/* Mobile Filter Toggle */}
        <Box display={{ xs: "block", md: "none" }} width="100%">
          <Button
            fullWidth
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            variant="outlined"
            startIcon={<TuneIcon />}
            sx={{
              justifyContent: "center",
              py: 1,
              mb: 1,
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.15)"
                  : "rgba(0,0,0,0.15)",
              color: colors.gray[100],
              "&:hover": {
                borderColor: colors.blueAccent[500],
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(0,0,0,0.02)",
              },
            }}
          >
            {mobileMenuOpen ? "Hide Actions & Filters" : "Actions & Filters"}
          </Button>

          <Collapse in={mobileMenuOpen}>
            <Box
              display="flex"
              flexDirection="column"
              gap="10px"
              mb={2}
              p={2}
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.02)"
                    : "#f8fafc",
                borderRadius: "8px",
                border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"}`,
              }}
            >
              <FormControl size="small" fullWidth>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "#ffffff",
                    color: colors.gray[100],
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "none"
                        : "0 1px 3px rgba(0,0,0,0.05)",
                    transition: "all 0.2s ease",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.15)"
                          : "rgba(0,0,0,0.15)",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.blueAccent[400],
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.blueAccent[500],
                    },
                  }}
                  MenuProps={{
                    TransitionComponent: Fade,
                    transitionDuration: 300,
                    PaperProps: {
                      sx: {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                            : "rgba(255, 255, 255, 0.95)",
                        backdropFilter: "blur(16px)",
                        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}`,
                        boxShadow:
                          theme.palette.mode === "dark"
                            ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                            : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                        borderRadius: "12px",
                        mt: 1,
                        "& .MuiMenuItem-root": {
                          padding: "10px 20px",
                          margin: "4px 8px",
                          borderRadius: "8px",
                          transition: "all 0.2s ease-in-out",
                          color: colors.gray[100],
                          "&:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(0, 0, 0, 0.04)",
                          },
                          "&:active": { transform: "scale(0.95)" },
                          "&.Mui-selected": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.15)"
                                : "rgba(0, 0, 0, 0.08)",
                            fontWeight: "bold",
                          },
                          "&.Mui-selected:hover": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.2)"
                                : "rgba(0, 0, 0, 0.12)",
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">Past 7 Days</MenuItem>
                  <MenuItem value="14days">Past 14 Days</MenuItem>
                  <MenuItem value="month">Past 30 Days</MenuItem>
                  <MenuItem value="3months">Past 3 Months</MenuItem>
                  <MenuItem value="6months">Past 6 Months</MenuItem>
                  <MenuItem value="year">Past Year</MenuItem>
                  <MenuItem value="all">All Time</MenuItem>
                </Select>
              </FormControl>

              <Button
                fullWidth
                variant="contained"
                onClick={handleClickOpen}
                startIcon={<AddOutlinedIcon />}
                sx={{
                  backgroundColor: colors.blueAccent[700],
                  color: "#ffffff",
                  py: 1,
                  "&:hover": { backgroundColor: colors.blueAccent[800] },
                }}
              >
                Add Record
              </Button>

              {admin && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => handleOpenDialog(null)}
                  startIcon={<DeleteOutlinedIcon />}
                  sx={{
                    borderColor: colors.redAccent[500],
                    color: colors.redAccent[500],
                    py: 1,
                    "&:hover": {
                      backgroundColor: colors.redAccent[600],
                      color: "#ffffff",
                    },
                  }}
                >
                  Delete All
                </Button>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Desktop/Tablet Controls */}
        <Box
          display={{ xs: "none", md: "flex" }}
          flexDirection="row"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="flex-end"
          width="auto"
          gap={{ md: "8px", lg: "15px" }}
          sx={{ ml: "auto", flexShrink: 0 }}
        >
          <FormControl
            size="small"
            sx={{ width: "auto", minWidth: { md: 120, lg: 150 } }}
          >
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              sx={{
                height: { md: "38px", lg: "42px" },
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#ffffff",
                color: colors.gray[100],
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "none"
                    : "0 1px 3px rgba(0,0,0,0.05)",
                transition: "all 0.2s ease",
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.15)"
                      : "rgba(0,0,0,0.15)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[400],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[500],
                },
              }}
              MenuProps={{
                TransitionComponent: Fade,
                transitionDuration: 300,
                PaperProps: {
                  sx: {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                        : "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(16px)",
                    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}`,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                    borderRadius: "12px",
                    mt: 1,
                    "& .MuiMenuItem-root": {
                      padding: "10px 20px",
                      margin: "4px 8px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease-in-out",
                      color: colors.gray[100],
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                      "&:active": { transform: "scale(0.95)" },
                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.08)",
                        fontWeight: "bold",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(0, 0, 0, 0.12)",
                      },
                    },
                  },
                },
              }}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">Past 7 Days</MenuItem>
              <MenuItem value="14days">Past 14 Days</MenuItem>
              <MenuItem value="month">Past 30 Days</MenuItem>
              <MenuItem value="3months">Past 3 Months</MenuItem>
              <MenuItem value="6months">Past 6 Months</MenuItem>
              <MenuItem value="year">Past Year</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>

          {admin && (
            <Button
              sx={{
                height: { md: "38px", lg: "42px" },
                width: "auto",
                minWidth: { md: "auto", lg: 150 },
                backgroundColor: colors.redAccent[600],
                color: "#ffffff",
                fontSize: { md: "0.8rem", lg: "0.875rem" },
                fontWeight: "bold",
                padding: { md: "0 12px", lg: "0 20px" },
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.redAccent[700],
                  transform: "translateY(-1px)",
                },
                "&:active": { transform: "scale(0.95)" },
              }}
              onClick={() => handleOpenDialog(null)}
            >
              <DeleteOutlinedIcon
                sx={{
                  mr: { md: "4px", lg: "8px" },
                  fontSize: { md: "1.125rem", lg: "1.375rem" },
                }}
              />
              Delete All
            </Button>
          )}

          <Button
            sx={{
              height: { md: "38px", lg: "42px" },
              width: "auto",
              minWidth: { md: "auto", lg: 150 },
              backgroundColor: colors.blueAccent[700],
              color: "#ffffff",
              fontSize: { md: "0.8rem", lg: "0.875rem" },
              fontWeight: "bold",
              padding: { md: "0 12px", lg: "0 20px" },
              boxShadow:
                theme.palette.mode === "dark"
                  ? "none"
                  : "0 2px 5px rgba(79, 70, 229, 0.2)",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: colors.blueAccent[800],
                transform: "translateY(-1px)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
            onClick={handleClickOpen}
          >
            <AddOutlinedIcon
              sx={{
                mr: { md: "4px", lg: "8px" },
                fontSize: { md: "1.125rem", lg: "1.375rem" },
              }}
            />
            Add Record
          </Button>

          <ExpensesRecordForm
            open={open}
            handleClose={handleClose}
            onRecordAdded={handleAddRecord}
            editRecord={editRecord}
            onRecordEdited={handleRecordEdited}
          />
        </Box>
      </Box>

      {}
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          md: admin ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
        }}
        gap="20px"
        mb={{ xs: "25px", md: "40px" }}
        sx={{
          opacity: 0,
          animation: "slideInUp 0.5s ease-out forwards",
          animationDelay: "0.15s",
        }}
      >
        <Box
          p={{ xs: "16px 20px", sm: "20px 24px" }}
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "15px",
          }}
        >
          <Box sx={{ color: colors.blueAccent[500] }}>
            <ReceiptLongOutlinedIcon
              sx={{
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.1875rem" },
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              color={colors.gray[400]}
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="1px"
              mb="4px"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" } }}
            >
              Total Transactions
            </Typography>
            {isLoading ? (
              <Skeleton
                variant="rounded"
                width={80}
                height={{ xs: 24, sm: 28, md: 32 }}
                animation="wave"
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.06)",
                }}
              />
            ) : (
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "800",
                  color: colors.blueAccent[500],
                  fontSize: { xs: "1.1rem", sm: "1.15rem", md: "1.25rem" },
                }}
              >
                {rows.length.toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          p={{ xs: "16px 20px", sm: "20px 24px" }}
          sx={{
            backgroundColor:
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
            borderRadius: "12px",
            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
            boxShadow:
              theme.palette.mode === "dark"
                ? "0 4px 20px rgba(0,0,0,0.2)"
                : "0 4px 20px rgba(0,0,0,0.03)",
            display: "flex",
            alignItems: "center",
            gap: "15px",
          }}
        >
          <Box sx={{ color: colors.blueAccent[500] }}>
            <Inventory2OutlinedIcon
              sx={{
                fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.1875rem" },
              }}
            />
          </Box>
          <Box>
            <Typography
              variant="h6"
              color={colors.gray[400]}
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="1px"
              mb="4px"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" } }}
            >
              Total Items Expensed
            </Typography>
            {isLoading ? (
              <Skeleton
                variant="rounded"
                width={100}
                height={{ xs: 24, sm: 28, md: 32 }}
                animation="wave"
                sx={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(0,0,0,0.06)",
                }}
              />
            ) : (
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "800",
                  color: colors.blueAccent[500],
                  fontSize: { xs: "1.1rem", sm: "1.15rem", md: "1.25rem" },
                }}
              >
                {rows
                  .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
                  .toLocaleString()}
              </Typography>
            )}
          </Box>
        </Box>

        {admin && (
          <Box
            p={{ xs: "16px 20px", sm: "20px 24px" }}
            sx={{
              gridColumn: { sm: "span 2", md: "span 1" },
              backgroundColor:
                theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
              borderRadius: "12px",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 4px 20px rgba(0,0,0,0.2)"
                  : "0 4px 20px rgba(0,0,0,0.03)",
              display: "flex",
              alignItems: "center",
              gap: "15px",
            }}
          >
            <Box sx={{ color: colors.redAccent[500] }}>
              <AccountBalanceWalletOutlinedIcon
                sx={{
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2.1875rem" },
                }}
              />
            </Box>
            <Box>
              <Typography
                variant="h6"
                color={colors.gray[400]}
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="1px"
                mb="4px"
                sx={{
                  fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" },
                }}
              >
                Total Expenses Balance
              </Typography>
              {isLoading ? (
                <Skeleton
                  variant="rounded"
                  width={140}
                  height={{ xs: 24, sm: 28, md: 32 }}
                  animation="wave"
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                  }}
                />
              ) : (
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: "800",
                    color: colors.redAccent[500],
                    fontSize: { xs: "1.1rem", sm: "1.15rem", md: "1.25rem" },
                  }}
                >
                  ₦{totalBalanceSum.toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {}
      <Box
        height="70vh"
        display="flex"
        flexDirection="column"
        sx={{
          opacity: 0,
          animation: "slideInUp 0.5s ease-out forwards",
          animationDelay: "0.3s",
          backgroundColor:
            theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
          borderRadius: "12px",
          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0,0,0,0.2)"
              : "0 4px 20px rgba(0,0,0,0.03)",
          overflow: "hidden",
          "& .MuiDataGrid-root": {
            border: "none",
            "--DataGrid-containerBackground":
              theme.palette.mode === "dark" ? colors.primary[400] : "#ffffff",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
          },
          "& .name-column--cell": {
            color: colors.greenAccent[500],
            fontWeight: 600,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.01)",
            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
            color: colors.gray[300],
            fontSize: "0.75rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "1px",
          },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.02)"
                : "rgba(0,0,0,0.01)",
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[500]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.gray[100]} !important`,
            margin: "0 !important",
            fontSize: "14px",
            [theme.breakpoints.down("sm")]: {
              minWidth: "36px !important",
              padding: "6px !important",
              fontSize: "0px !important",
              "& .MuiButton-startIcon": {
                margin: "0 !important",
              },
              "& .MuiSvgIcon-root": {
                fontSize: "1.25rem !important",
              },
            },
          },
          [theme.breakpoints.down("sm")]: {
            "& .MuiDataGrid-scrollbar": {
              display: "none",
            },
            "& .MuiDataGrid-virtualScroller, & .MuiDataGrid-main": {
              scrollbarWidth: "none" /* Firefox */,
              msOverflowStyle: "none" /* IE and Edge */,
              "&::-webkit-scrollbar": { display: "none" } /* Chrome/Safari */,
            },
          },
        }}
      >
        <Box flexGrow={1} sx={{ minHeight: 0, minWidth: 0, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowClassName={getRowClassName}
            loading={isLoading}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: CustomNoRowsOverlay,
              noResultsOverlay: CustomNoRowsOverlay,
              loadingOverlay: () => (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  {[...Array(12)].map((_, index) => (
                    <Box
                      key={index}
                      display="flex"
                      alignItems="center"
                      gap="16px"
                      sx={{
                        width: "100%",
                        height: "52px",
                        padding: "0 10px",
                        borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                      }}
                    >
                      {[
                        0.25, 0.5, 0.5, 0.4, 0.5, 0.5, 0.75, 0.6, 0.55, 0.5,
                      ].map((flexValue, colIndex) => (
                        <Skeleton
                          key={colIndex}
                          variant="rounded"
                          animation="wave"
                          height={20}
                          sx={{
                            flex: flexValue,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.06)",
                          }}
                        />
                      ))}
                    </Box>
                  ))}
                </Box>
              ),
            }}
            slotProps={{
              toolbar: { searchQuery, setSearchQuery, colors },
              basePopper: {
                sx: {
                  "& .MuiPaper-root": {
                    animation: "popperFadeIn 0.3s ease-out",
                    "@keyframes popperFadeIn": {
                      "0%": { opacity: 0, transform: "translateY(-4px)" },
                      "100%": { opacity: 1, transform: "translateY(0)" },
                    },
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                        : "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(16px)",
                    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}`,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                    borderRadius: "12px",
                    mt: 1,
                    "& .MuiDataGrid-panelContent": { padding: "8px" },
                    "& .MuiDataGrid-panelFooter": {
                      borderTop: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.05)"}`,
                      padding: "8px",
                    },
                    "& .MuiButton-root": {
                      textTransform: "none",
                      borderRadius: "8px",
                      fontWeight: 600,
                      padding: "6px 12px",
                      transition: "all 0.2s ease-in-out",
                    },
                    "& .MuiMenuItem-root": {
                      padding: "10px 20px",
                      margin: "4px 8px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease-in-out",
                      color: colors.gray[100],
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                      "&:active": { transform: "scale(0.95)" },
                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.08)",
                        fontWeight: "bold",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(0, 0, 0, 0.12)",
                      },
                    },
                  },
                },
              },
              baseMenu: {
                TransitionComponent: Fade,
                transitionDuration: 300,
                PaperProps: {
                  sx: {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
                        : "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(16px)",
                    border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}`,
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
                        : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
                    borderRadius: "12px",
                    mt: 1,
                    "& .MuiMenuItem-root": {
                      padding: "10px 20px",
                      margin: "4px 8px",
                      borderRadius: "8px",
                      transition: "all 0.2s ease-in-out",
                      color: colors.gray[100],
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.1)"
                            : "rgba(0, 0, 0, 0.04)",
                      },
                      "&:active": { transform: "scale(0.95)" },
                      "&.Mui-selected": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.15)"
                            : "rgba(0, 0, 0, 0.08)",
                        fontWeight: "bold",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(255, 255, 255, 0.2)"
                            : "rgba(0, 0, 0, 0.12)",
                      },
                    },
                  },
                },
              },
            }}
          />
        </Box>
      </Box>

      <ExpensesDeleteDialog
        open={openDialog}
        onClose={handleCloseDialog}
        onConfirm={handleDeleteRecord}
        isBulkDelete={isBulkDelete}
      />
    </Box>
  );
};

export default Expenses;
